/** Curated Lucide keys for reservation channel CTAs (PascalCase = lucide-react export). */

export type CtaLucideGroupId =
  | 'booking'
  | 'communication'
  | 'actions'
  | 'food'
  | 'place'
  | 'people'
  | 'social';

export interface CtaLucideCatalogEntry {
  /** PascalCase Lucide export name (stored in `content.ctaIcon`). */
  key: string;
  group: CtaLucideGroupId;
  /** Short English label for the picker. */
  label: string;
}

export const CTA_LUCIDE_GROUP_LABELS: Record<CtaLucideGroupId, string> = {
  booking: 'Booking',
  communication: 'Communication',
  actions: 'Actions',
  food: 'Food & drink',
  place: 'Place',
  people: 'People',
  social: 'Social brands',
};

/** Channel-default icon keys when `ctaIcon` is empty. */
export const CTA_LUCIDE_CHANNEL_DEFAULTS = {
  'third-party': 'CalendarCheck',
  messenger: 'MessageCircle',
  call: 'Phone',
} as const;

/**
 * Hospitality CTA catalog — Lucide collections subset with React + SSR parity.
 * Keys must exist in `ctaLucideMap` and `LUCIDE_SSR_BY_ICON_KEY`.
 */
export const CTA_LUCIDE_CATALOG: readonly CtaLucideCatalogEntry[] = [
  { key: 'CalendarCheck', group: 'booking', label: 'Calendar check' },
  { key: 'Calendar', group: 'booking', label: 'Calendar' },
  { key: 'CalendarClock', group: 'booking', label: 'Calendar clock' },
  { key: 'CalendarDays', group: 'booking', label: 'Calendar days' },
  { key: 'CalendarPlus', group: 'booking', label: 'Calendar plus' },
  { key: 'Ticket', group: 'booking', label: 'Ticket' },

  { key: 'MessageCircle', group: 'communication', label: 'Message' },
  { key: 'MessageSquare', group: 'communication', label: 'Message square' },
  { key: 'MessagesSquare', group: 'communication', label: 'Messages' },
  { key: 'Phone', group: 'communication', label: 'Phone' },
  { key: 'PhoneCall', group: 'communication', label: 'Phone call' },
  { key: 'PhoneOutgoing', group: 'communication', label: 'Phone outgoing' },
  { key: 'Smartphone', group: 'communication', label: 'Smartphone' },
  { key: 'Mail', group: 'communication', label: 'Mail' },
  { key: 'Send', group: 'communication', label: 'Send' },

  { key: 'ExternalLink', group: 'actions', label: 'External link' },
  { key: 'Link2', group: 'actions', label: 'Link' },
  { key: 'ArrowRight', group: 'actions', label: 'Arrow right' },
  { key: 'ArrowUp', group: 'actions', label: 'Arrow up' },
  { key: 'ArrowUpRight', group: 'actions', label: 'Arrow up right' },
  { key: 'Share2', group: 'actions', label: 'Share' },
  { key: 'CircleCheck', group: 'actions', label: 'Check' },
  { key: 'Sparkles', group: 'actions', label: 'Sparkles' },

  { key: 'Utensils', group: 'food', label: 'Utensils' },
  { key: 'UtensilsCrossed', group: 'food', label: 'Utensils crossed' },
  { key: 'ConciergeBell', group: 'food', label: 'Concierge bell' },
  { key: 'Wine', group: 'food', label: 'Wine' },
  { key: 'Coffee', group: 'food', label: 'Coffee' },
  { key: 'Martini', group: 'food', label: 'Martini' },
  { key: 'ChefHat', group: 'food', label: 'Chef hat' },

  { key: 'MapPin', group: 'place', label: 'Map pin' },
  { key: 'MapPinned', group: 'place', label: 'Map pinned' },
  { key: 'Store', group: 'place', label: 'Store' },
  { key: 'Building2', group: 'place', label: 'Building' },
  { key: 'Home', group: 'place', label: 'Home' },
  { key: 'Clock', group: 'place', label: 'Clock' },

  { key: 'Users', group: 'people', label: 'Users' },
  { key: 'UserRound', group: 'people', label: 'User' },
  { key: 'Heart', group: 'people', label: 'Heart' },
  { key: 'Star', group: 'people', label: 'Star' },

  { key: 'FileText', group: 'actions', label: 'File text' },
] as const;

/** Defaults when section / fact / PDF icon content is empty. */
export const SECTION_LUCIDE_DEFAULTS = {
  hours: 'Clock',
  location: 'MapPin',
  pdf: 'FileText',
  faq: 'MessageCircle',
} as const;

const CTA_LUCIDE_KEY_SET = new Set(CTA_LUCIDE_CATALOG.map((entry) => entry.key));

export function isCtaLucideIconKey(iconKey: string | null | undefined): boolean {
  return Boolean(iconKey && CTA_LUCIDE_KEY_SET.has(iconKey));
}
